import httpApis from './HttpApis'; 

let sessionId = null;

export const initializeAnalytics = () => {
  sessionId = generateSessionId();
};

const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const getUserId = () => {
  return localStorage.getItem('userId') || '';
};

const getDeviceId = () => {
  let deviceId = localStorage.getItem('deviceId');
    localStorage.setItem('deviceId', deviceId);
     return deviceId;
  }
 
const getPlatform = () => 'web';


const getAppVersion = () => {
  return process.env.REACT_APP_VERSION || '1.0.0';
};

const callAnalyticsAPI = async (params, eventType) => {
  try {
    await httpApis.Analytics(params, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  } catch (error) {
    console.error(`Analytics error for ${eventType}:`, error);
  }
};


const sendAnalyticsEvent = async (eventType, eventCategory, payloadData = {}) => {
  if (!sessionId) {
    initializeAnalytics();
  }
  
  const params = {
    eventType,
    eventCategory,
    sessionId,
    userId: getUserId(),
    deviceId: getDeviceId(),
    platform: getPlatform(),
    appVersion: getAppVersion(),
    payload: payloadData
  };

  await callAnalyticsAPI(params, eventType);
};

// USER EVENTS
export const trackUserRegistered = async () => {
  return await sendAnalyticsEvent('USER_REGISTERED', 'USER', {});
};

export const trackUserLogin = async () => {
  return await sendAnalyticsEvent('USER_LOGIN', 'USER', {});
};

export const trackUserLogout = async () => {
  return await sendAnalyticsEvent('USER_LOGOUT', 'USER', {});
};

export const trackUserProfileUpdate = async () => {
  return await sendAnalyticsEvent('USER_PROFILE_UPDATE', 'USER', {});
};

export const trackUserSearch = async (keyword) => {
  return await sendAnalyticsEvent('USER_SEARCH', 'USER', { keyword });
};

export const trackUserClick = async (clickData = {}) => {
  return await sendAnalyticsEvent('USER_CLICK', 'USER', clickData);
};

export const trackWatchlistAdd = async (contentId) => {
  return await sendAnalyticsEvent('USER_WATCHLIST_ADD', 'USER', { contentId });
};

export const trackWatchlistRemove = async (contentId) => {
  return await sendAnalyticsEvent('USER_WATCHLIST_REMOVE', 'USER', { contentId });
};


// PLAYBACK EVENTS (with nested payload)

// Every play was reaching the backend as two PLAYBACK_START events 1-6s apart
// (the player's own playbackStartedRef guard is per-mount, so a remount of the
// player sends another). Kept at module level so it survives remounts: a START
// for a video that's still open (no STOP/COMPLETE since) within the window is
// dropped. The backend dedupes the same way for older clients.
const DUPLICATE_START_WINDOW_MS = 30000;
const openPlaybackStarts = new Map(); // video_slug -> Date.now() of last START sent

export const trackPlaybackStart = async (video_slug, position = 0) => {
  const lastStart = openPlaybackStarts.get(video_slug);
  if (lastStart && Date.now() - lastStart < DUPLICATE_START_WINDOW_MS) return;
  openPlaybackStarts.set(video_slug, Date.now());
  return await sendAnalyticsEvent('PLAYBACK_START', 'PLAYBACK', { video_slug, position }, true);
};

export const trackPlaybackPause = async (video_slug, position) => {
  return await sendAnalyticsEvent('PLAYBACK_PAUSE', 'PLAYBACK', { video_slug, position }, true);
};

export const trackPlaybackResume = async (video_slug, position) => {
  return await sendAnalyticsEvent('PLAYBACK_RESUME', 'PLAYBACK', { video_slug, position }, true);
};

export const trackPlaybackComplete = async (video_slug, position) => {
  openPlaybackStarts.delete(video_slug);
  return await sendAnalyticsEvent('PLAYBACK_COMPLETE', 'PLAYBACK', { video_slug, position }, true);
};

export const trackPlaybackStop = async (video_slug, position) => {
  openPlaybackStarts.delete(video_slug);
  return await sendAnalyticsEvent('PLAYBACK_STOP', 'PLAYBACK', { video_slug, position }, true);
};

export const trackPlaybackSeek = async (video_slug, from_position, to_position, seek_type, seek_by) => {
  return await sendAnalyticsEvent('PLAYBACK_SEEK', 'PLAYBACK', { 
    video_slug, 
    from_position, 
    to_position, 
    seek_type, 
    seek_by 
  }, true);
};

export const trackPlaybackError = async (video_slug, error_code, error_message) => {
  return await sendAnalyticsEvent('PLAYBACK_ERROR', 'PLAYBACK', { 
    video_slug, 
    error_code, 
    error_message 
  }, true);
};

// SUBSCRIPTION EVENTS (with nested payload)
export const trackSubscriptionStarted = async (subscriptionPlan, userType) => {
  return await sendAnalyticsEvent('SUBSCRIPTION_STARTED', 'SUBSCRIPTION', { 
    subscriptionPlan, 
    userType 
  }, true);
};


export const trackSubscriptionCanceled = async (cancelData = {}) => {
  return await sendAnalyticsEvent('SUBSCRIPTION_CANCELED', 'SUBSCRIPTION', cancelData, true);
};


export const trackPaymentSuccess = async (paymentData = {}) => {
  return await sendAnalyticsEvent('PAYMENT_SUCCESS', 'SUBSCRIPTION', paymentData, true);
};

export const trackPaymentFailed = async (paymentData = {}) => {
  return await sendAnalyticsEvent('PAYMENT_FAILED', 'SUBSCRIPTION', paymentData, true);
};

// QOE (Quality of Experience) EVENTS (with nested payload)
export const trackStartupTime = async (duration) => {
  return await sendAnalyticsEvent('QOE_STARTUP_TIME', 'QOE', { duration }, true);
};

export const trackBufferRatio = async (video_slug, duration) => {
  return await sendAnalyticsEvent('QOE_BUFFER_RATIO', 'QOE', { video_slug, duration }, true);
};

export const trackPlaybackFailure = async (failureData = {}) => {
  return await sendAnalyticsEvent('QOE_PLAYBACK_FAILURE', 'QOE', failureData, true);
};


// APP LIFECYCLE EVENTS
export const trackAppOpen = async (appData = {}) => {
  return await sendAnalyticsEvent('APP_OPEN', 'APP_LIFECYCLE', appData);
};

export const trackAppClose = async (appData = {}) => {
  return await sendAnalyticsEvent('APP_CLOSE', 'APP_LIFECYCLE', appData);
};

// NAVIGATION EVENTS
export const trackScreenView = async (screenData = {}) => {
  return await sendAnalyticsEvent('SCREEN_VIEW', 'NAVIGATION', screenData);
};
